import { makeStyles } from "makeStyles";
import { PermissionGroup } from "../models";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { permissionsTableStyles } from "../styles";
import SingleValues from "../SingleValues";
import { useState, useMemo } from "react";
import { TableSortOrder, getComparator, SimpleSortableTableHeadProps, SortableTableHead } from "../SortableTableHead";
import GroupsPermissions from "./GroupsPermissions";
import GroupsInas from "./GroupsInas";

const useStyles = makeStyles()((theme) => ({
  ...permissionsTableStyles(theme),
  groupCell: {
    width: "220px"
  },
  membersCell: {
    width: "220px"
  },
  assignmentsCell: {
    width: "600px"
  }
}));

interface Props {
  groups?: PermissionGroup[];
}

const AppAccessGroups: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles();

  const [order, setOrder] = useState<TableSortOrder>("asc");
  const [orderBy, setOrderBy] = useState<keyof PermissionGroup>("name");

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof PermissionGroup) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const displayRows = useMemo(() => [...(props.groups ?? [])].sort(getComparator(order, orderBy)), [order, orderBy, props.groups]);

  function GroupsTableHead({ id, label, className }: SimpleSortableTableHeadProps<PermissionGroup>) {
    return (
      <SortableTableHead
        className={className}
        headCell={{
          id,
          label
        }}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
    );
  }

  return (
    <TableContainer component={Paper} className={classes.topLevelTableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <GroupsTableHead id="name" label="Group Name" className={classes.groupCell} />
            <TableCell className={classes.membersCell}>Members</TableCell>
            <TableCell className={classes.assignmentsCell}>INA Assignments</TableCell>
            <TableCell>Permissions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows?.map((group) => (
            <TableRow key={group.name}>
              <TableCell>{group.name}</TableCell>
              <TableCell>
                {" "}
                {group.members?.length === 0 ? (
                  "--"
                ) : group.members?.length === 1 ? (
                  group.members[0].name
                ) : (
                  <details>
                    <summary>
                      {group.members!.length} Member{group.members!.length > 1 ? "s" : ""}:
                    </summary>
                    <SingleValues values={group.members?.map((m) => m.name)} />
                  </details>
                )}
              </TableCell>
              <TableCell>
                {group.inas?.length === 0 ? (
                  "--"
                ) : (
                  <details>
                    <summary>
                      {group.inas?.length} INA{group.inas?.length !== 1 ? "s" : ""}:
                    </summary>
                    <GroupsInas inas={group.inas} />
                  </details>
                )}
              </TableCell>
              <TableCell>
                <details>
                  <summary>
                    {group.permissions?.length} Permission{group.permissions?.length !== 1 ? "s" : ""}:
                  </summary>
                  <GroupsPermissions permissions={group.permissions} />
                </details>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppAccessGroups;
